import { ChangeEvent, ReactNode, RefObject, createRef } from "react";
import { ViewItem, viewItemProps } from "../../../../Components/Views/ViewItem";
import { MailHtmlFrame, sanitizeEmailHtml } from "../../../../Components/Views/MailHtmlFrame";
import { controllers } from "../../../../Controllers/Controllers";
import { navigator } from "../../../../Controllers/Navigator";
import { LeadCategoryType, LeadType } from "../../../../DataTypes/leads";
import {
    EmailTemplateFormFields,
    EmailTemplatePreviewResult,
    KNOWN_TEMPLATE_PLACEHOLDERS,
    emptyEmailTemplateFields,
} from "../../../../DataTypes/templates";
import '../outreach-styles.css'
import '../Categories/categories-styles.css'
import '../LeadForm/lead-form-styles.css'
import '../../Settings/settings-styles.css'
import './template-form-styles.css'

type InsertableField = 'subject' | 'body_html' | 'body_text'

const FIELD_LABELS: Record<InsertableField, string> = {
    subject: 'Subject',
    body_html: 'HTML Body',
    body_text: 'Text Body',
}

// Mirrors outreach/models.py's KNOWN_SIGNATURE_PLACEHOLDERS. If your
// codebase already defines this elsewhere (e.g. alongside the shared
// company-wide signature settings page, which this file already borrows
// CSS classes from), import it from there instead of duplicating it here.
const KNOWN_SIGNATURE_PLACEHOLDERS = [
    'lead_manager_name',
    'lead_manager_position',
    'lead_manager_email',
    'lead_manager_phone',
    'unsubscribe_url',
] as const

// Detects {{token}} patterns client-side so a typo shows up immediately
// while editing, without waiting on a Preview round-trip to the backend
// (which only tells you about unrecognized tokens in the saved version).
const findUnrecognizedPlaceholders = (fields: EmailTemplateFormFields): string[] => {
    const raw = `${fields.subject}\n${fields.body_html}\n${fields.body_text}`
    const found = new Set(Array.from(raw.matchAll(/\{\{([a-zA-Z0-9_]+)\}\}/g)).map(m => m[1]))
    const known = new Set(KNOWN_TEMPLATE_PLACEHOLDERS)
    return Array.from(found).filter(token => !known.has(token))
}

// Same idea, but for the signature field's own (different) placeholder
// namespace — {{lead_manager_name}}, {{unsubscribe_url}}, etc. — which
// isn't a valid token in the subject/body fields and vice versa.
const findUnrecognizedSignaturePlaceholders = (html: string): string[] => {
    const found = new Set(Array.from(html.matchAll(/\{\{([a-zA-Z0-9_]+)\}\}/g)).map(m => m[1]))
    const known = new Set<string>(KNOWN_SIGNATURE_PLACEHOLDERS)
    return Array.from(found).filter(token => !known.has(token))
}

// A blank custom signature isn't our concern here — it just means this
// template falls back to the shared company signature. And for a
// NON-promotional template, the whole check is moot: the backend never
// generates a working unsubscribe link for those sends regardless of
// what the signature HTML contains (see EmailTemplate.is_promotional),
// so requiring {{unsubscribe_url}} there would be enforcing something
// that doesn't apply. Only a PROMOTIONAL template with a non-blank
// custom signature that omits {{unsubscribe_url}} is a hard error —
// that combination would send real recipients marketing email with no
// way to opt out.
const customSignatureMissingUnsubscribe = (html: string, isPromotional: boolean): boolean => {
    if (!isPromotional) return false
    const trimmed = html.trim()
    if (!trimmed) return false
    return !trimmed.includes('{{unsubscribe_url}}')
}

type TemplateFormPageProps = viewItemProps & {
    templateId?: string;
}

type TemplateFormPageState = {
    fields: EmailTemplateFormFields;
    categories: LeadCategoryType[];
    currentTemplateId: number | null;
    loading: boolean;
    needsAuth: boolean;
    loadError: string | null;
    saving: boolean;
    saveError: string | null;
    saveMessage: string | null;

    lastFocusedField: InsertableField;

    previewOpen: boolean;
    previewLoading: boolean;
    previewError: string | null;
    previewResult: EmailTemplatePreviewResult | null;
    previewLeads: LeadType[];
    previewLeadId: string;
}

class TemplateFormPage extends ViewItem {
    props: TemplateFormPageProps;
    state: TemplateFormPageState;
    initialIsPublic: boolean = false;
    subjectRef: RefObject<HTMLInputElement | null> = createRef();
    bodyHtmlRef: RefObject<HTMLTextAreaElement | null> = createRef();
    bodyTextRef: RefObject<HTMLTextAreaElement | null> = createRef();
    fileInputRef: RefObject<HTMLInputElement | null> = createRef();
    signatureRef: RefObject<HTMLTextAreaElement | null> = createRef();
    signatureFileInputRef: RefObject<HTMLInputElement | null> = createRef();

    constructor(props: TemplateFormPageProps) {
        super(props)
        this.props = props
        this.state = {
            fields: { ...emptyEmailTemplateFields },
            categories: [],
            currentTemplateId: props.templateId ? Number(props.templateId) : null,
            loading: true,
            needsAuth: false,
            loadError: null,
            saving: false,
            saveError: null,
            saveMessage: null,

            lastFocusedField: 'body_html',

            previewOpen: false,
            previewLoading: false,
            previewError: null,
            previewResult: null,
            previewLeads: [],
            previewLeadId: '',
        }
    }

    get isEditMode(): boolean {
        return this.state.currentTemplateId !== null
    }

    componentDidMount(): void {
        this.loadInitialData()
    }

    loadInitialData = async () => {
        this.setState({ loading: true, loadError: null, needsAuth: false })

        if (!controllers().auth.userToken) {
            this.setState({ loading: false, needsAuth: true })
            return
        }

        const categoriesResponse = await controllers().outreach.getCategories()
        const categories = (categoriesResponse && categoriesResponse.statusCode === 200)
            ? (categoriesResponse.data?.categories || [])
            : []

        if (!this.state.currentTemplateId) {
            this.setState({ loading: false, categories })
            return
        }

        // There's no single-template GET endpoint — only list/add/edit/delete
        // — so editing an existing template means finding it in the full list.
        const templatesResponse = await controllers().mail.getTemplates()
        const template = (templatesResponse?.data?.templates || []).find(
            (t: any) => t.id === this.state.currentTemplateId
        )
        if (!templatesResponse || templatesResponse.statusCode !== 200 || !template) {
            this.setState({
                loading: false,
                categories,
                loadError: templatesResponse?.error || 'Failed to load this template.',
            })
            return
        }

        this.initialIsPublic = !!template.is_public
        this.setState({
            loading: false,
            categories,
            fields: {
                name: template.name || '',
                category_id: template.category?.id ? String(template.category.id) : '',
                subject: template.subject || '',
                body_html: template.body_html || '',
                body_text: template.body_text || '',
                is_public: !!template.is_public,
                is_promotional: !!template.is_promotional,
                custom_signature_html: template.custom_signature_html || '',
            },
        })
    }

    setField = (field: keyof EmailTemplateFormFields, value: string | boolean) => {
        this.setState((prev: TemplateFormPageState) => ({
            fields: { ...prev.fields, [field]: value },
            saveMessage: null,
        }))
    }

    handleTextChange = (field: keyof EmailTemplateFormFields) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        this.setField(field, e.target.value)
    }

    focusField = (field: InsertableField) => {
        this.setState({ lastFocusedField: field })
    }

    // Inserts at the cursor of whichever tracked field was last focused
    // (defaulting to the HTML body), then restores focus + cursor position
    // right after it so repeated inserts land where you'd expect.
    insertPlaceholder = (token: string) => {
        const { lastFocusedField, fields } = this.state
        const refByField = { subject: this.subjectRef, body_html: this.bodyHtmlRef, body_text: this.bodyTextRef }
        const el = refByField[lastFocusedField].current
        const placeholder = `{{${token}}}`
        const current = fields[lastFocusedField]

        if (!el) {
            this.setField(lastFocusedField, `${current}${placeholder}`)
            return
        }

        const start = el.selectionStart ?? current.length
        const end = el.selectionEnd ?? current.length
        const next = current.slice(0, start) + placeholder + current.slice(end)
        this.setField(lastFocusedField, next)

        requestAnimationFrame(() => {
            el.focus()
            const cursor = start + placeholder.length
            el.setSelectionRange(cursor, cursor)
        })
    }

    // Same insert-at-cursor behavior as insertPlaceholder, but always
    // targets the signature textarea — there's only the one field, so no
    // need to track "last focused" the way subject/body_html/body_text do.
    insertSignaturePlaceholder = (token: string) => {
        const el = this.signatureRef.current
        const placeholder = `{{${token}}}`
        const current = this.state.fields.custom_signature_html

        if (!el) {
            this.setField('custom_signature_html', `${current}${placeholder}`)
            return
        }

        const start = el.selectionStart ?? current.length
        const end = el.selectionEnd ?? current.length
        const next = current.slice(0, start) + placeholder + current.slice(end)
        this.setField('custom_signature_html', next)

        requestAnimationFrame(() => {
            el.focus()
            const cursor = start + placeholder.length
            el.setSelectionRange(cursor, cursor)
        })
    }

    handleUploadClick = () => {
        this.fileInputRef.current?.click()
    }

    handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        e.target.value = ''
        if (!file) return

        const reader = new FileReader()
        reader.onload = () => {
            const content = typeof reader.result === 'string' ? reader.result : ''
            this.setField('body_html', content)
        }
        reader.readAsText(file)
    }

    handleSignatureUploadClick = () => {
        this.signatureFileInputRef.current?.click()
    }

    handleSignatureFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        e.target.value = ''
        if (!file) return

        const reader = new FileReader()
        reader.onload = () => {
            const content = typeof reader.result === 'string' ? reader.result : ''
            this.setField('custom_signature_html', content)
        }
        reader.readAsText(file)
    }

    handleCancel = () => {
        navigator().navigate('/outreach/templates')
    }

    // Shared by both the Save button and Preview (which persists first, so
    // the preview always reflects exactly what's currently in the form
    // rather than whatever was last saved).
    persist = async (): Promise<number | null> => {
        const { fields, currentTemplateId } = this.state
        if (!fields.name.trim()) {
            this.setState({ saveError: 'Name is required.' })
            return null
        }
        if (customSignatureMissingUnsubscribe(fields.custom_signature_html, fields.is_promotional)) {
            this.setState({
                saveError: 'This is marked promotional, so the custom signature must include {{unsubscribe_url}} so recipients can unsubscribe — add it, or clear the field to use the shared signature instead.',
            })
            return null
        }
        this.setState({ saving: true, saveError: null, saveMessage: null })

        const payload: any = {
            name: fields.name.trim(),
            category_id: fields.category_id ? Number(fields.category_id) : null,
            subject: fields.subject,
            body_html: fields.body_html,
            body_text: fields.body_text,
            custom_signature_html: fields.custom_signature_html,
            is_promotional: fields.is_promotional,
        }
        // edit_email_template 403s if is_public is present at all and you're
        // not an admin — even if the value matches what's already saved. Only
        // include it when it's actually changing. add_email_template has no
        // such trap (silently ignored from non-admins), so always send it there.
        if (currentTemplateId) {
            if (fields.is_public !== this.initialIsPublic) {
                payload.is_public = fields.is_public
            }
        } else {
            payload.is_public = fields.is_public
        }

        const response = currentTemplateId
            ? await controllers().mail.editTemplate(currentTemplateId, payload)
            : await controllers().mail.addTemplate(payload)

        if (!response || response.statusCode !== 200) {
            this.setState({ saving: false, saveError: response?.error || 'Failed to save template.' })
            return null
        }

        const savedId = response.data?.id ?? currentTemplateId
        this.initialIsPublic = fields.is_public
        this.setState({ saving: false, currentTemplateId: savedId, saveMessage: 'Template saved.' })
        return savedId
    }

    handleSave = async () => {
        const savedId = await this.persist()
        if (savedId) navigator().navigate('/outreach/templates')
    }

    handlePreview = async () => {
        if (!this.state.previewLeads.length) this.loadPreviewLeads()
        const savedId = await this.persist()
        if (!savedId) return
        this.setState({ previewOpen: true })
        this.loadPreview(savedId)
    }

    loadPreviewLeads = async () => {
        const response = await controllers().outreach.getLeads({ per_page: 50 })
        if (response && response.statusCode === 200) {
            this.setState({ previewLeads: response.data?.leads || [] })
        }
    }

    loadPreview = async (templateId: number) => {
        const { previewLeadId } = this.state
        this.setState({ previewLoading: true, previewError: null })
        const response = await controllers().mail.previewTemplate(templateId, previewLeadId ? Number(previewLeadId) : undefined)
        if (!response || response.statusCode !== 200) {
            this.setState({ previewLoading: false, previewError: response?.error || 'Failed to load preview.' })
            return
        }
        this.setState({ previewLoading: false, previewResult: response.data })
    }

    handlePreviewLeadChange = (e: ChangeEvent<HTMLSelectElement>) => {
        this.setState({ previewLeadId: e.target.value }, () => {
            if (this.state.currentTemplateId) this.loadPreview(this.state.currentTemplateId)
        })
    }

    renderPlaceholderBar = () => {
        const { lastFocusedField } = this.state
        return (
            <div className="template-placeholder-bar">
                <span className="template-placeholder-label">
                    Insert into <strong>{FIELD_LABELS[lastFocusedField]}</strong>:
                </span>
                <div className="template-placeholder-chips">
                    {KNOWN_TEMPLATE_PLACEHOLDERS.map(token => (
                        <button
                            key={token}
                            type="button"
                            className="template-placeholder-chip"
                            onClick={() => this.insertPlaceholder(token)}
                        >
                            {`{{${token}}}`}
                        </button>
                    ))}
                </div>
            </div>
        )
    }

    renderSignaturePlaceholderBar = () => (
        <div className="template-placeholder-bar">
            <span className="template-placeholder-label">
                Insert into <strong>Signature</strong>:
            </span>
            <div className="template-placeholder-chips">
                {KNOWN_SIGNATURE_PLACEHOLDERS.map(token => (
                    <button
                        key={token}
                        type="button"
                        className="template-placeholder-chip"
                        onClick={() => this.insertSignaturePlaceholder(token)}
                    >
                        {`{{${token}}}`}
                    </button>
                ))}
            </div>
        </div>
    )

    // Always part of the layout now (a permanent right-hand column, not a
    // toggleable block) — before the first "Save & Preview" it just shows a
    // prompt instead of nothing.
    renderPreview = () => {
        const { previewOpen, previewLoading, previewError, previewResult, previewLeads, previewLeadId } = this.state

        return (
            <div className="lead-form-section template-preview-panel">
                <h3 className="lead-form-section-title">Preview</h3>

                {!previewOpen && (
                    <p className="outreach-muted template-preview-empty">
                        Click "Save &amp; Preview" to see how this template will render.
                    </p>
                )}

                {previewOpen && (
                    <>
                        <div className="category-form-group">
                            <label className="category-form-label">Preview using</label>
                            <select className="category-form-input" value={previewLeadId} onChange={this.handlePreviewLeadChange}>
                                <option value="">Sample data</option>
                                {previewLeads.map(lead => (
                                    <option key={lead.id} value={lead.id}>{lead.company_name}</option>
                                ))}
                            </select>
                        </div>

                        {previewLoading && (
                            <div className="outreach-state">
                                <div className="outreach-spinner" />
                            </div>
                        )}

                        {!previewLoading && previewError && (
                            <div className="outreach-banner outreach-banner-error">
                                <span className="outreach-banner-text">{previewError}</span>
                            </div>
                        )}

                        {!previewLoading && previewResult && (
                            <>
                                {previewResult.unrecognized_placeholders.length > 0 && (
                                    <div className="outreach-banner outreach-banner-error">
                                        <span className="outreach-banner-text">
                                            Unrecognized placeholder{previewResult.unrecognized_placeholders.length === 1 ? '' : 's'}: {previewResult.unrecognized_placeholders.map(t => `{{${t}}}`).join(', ')} — these won't be replaced when sent.
                                        </span>
                                    </div>
                                )}

                                <div className="template-preview-subject">
                                    <span className="settings-signature-preview-label">Subject</span>
                                    <p>{previewResult.subject || <span className="outreach-muted">(none)</span>}</p>
                                </div>

                                {previewResult.body_html ? (
                                    <div className="settings-signature-preview">
                                        <span className="settings-signature-preview-label">HTML Body</span>
                                        <MailHtmlFrame html={sanitizeEmailHtml(previewResult.body_html)} />
                                    </div>
                                ) : previewResult.body_text ? (
                                    <div className="template-preview-text">
                                        <span className="settings-signature-preview-label">Text Body</span>
                                        <pre>{previewResult.body_text}</pre>
                                    </div>
                                ) : (
                                    <p className="outreach-muted">No body content.</p>
                                )}

                                {previewResult.signature && (
                                    <div className="template-preview-signature">
                                        <span className="settings-signature-preview-label">
                                            Signature ({previewResult.signature.source === 'custom' ? 'custom for this template' : 'shared'})
                                        </span>

                                        {previewResult.signature.unrecognized_placeholders.length > 0 && (
                                            <div className="outreach-banner outreach-banner-error">
                                                <span className="outreach-banner-text">
                                                    Unrecognized placeholder{previewResult.signature.unrecognized_placeholders.length === 1 ? '' : 's'}: {previewResult.signature.unrecognized_placeholders.map(t => `{{${t}}}`).join(', ')} — these won't be replaced when sent.
                                                </span>
                                            </div>
                                        )}

                                        {previewResult.signature.rendered_html ? (
                                            <div className="settings-signature-preview">
                                                <MailHtmlFrame html={sanitizeEmailHtml(previewResult.signature.rendered_html)} />
                                            </div>
                                        ) : (
                                            <p className="outreach-muted">No signature configured.</p>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        )
    }

    render(): ReactNode {
        const { fields, categories, loading, needsAuth, loadError, saving, saveError, saveMessage } = this.state
        const unrecognized = findUnrecognizedPlaceholders(fields)
        const unrecognizedSignature = findUnrecognizedSignaturePlaceholders(fields.custom_signature_html)
        const signatureMissingUnsubscribe = customSignatureMissingUnsubscribe(fields.custom_signature_html, fields.is_promotional)

        return (
            <div className="outreach-page">
                <div className="outreach-header">
                    <div className="outreach-title-header">
                        <h1 className="outreach-title">{this.isEditMode ? 'Edit Template' : 'New Template'}</h1>
                        <p className="outreach-subtitle">
                            {this.isEditMode ? 'Update this reusable email template.' : 'Create a reusable email template.'}
                        </p>
                    </div>
                </div>

                {!loading && needsAuth && (
                    <div className="outreach-banner outreach-banner-info">
                        <span className="outreach-banner-text">Sign in to manage templates.</span>
                        <button className="outreach-banner-action" onClick={() => controllers().modals.authModal.show()}>
                            Sign In
                        </button>
                    </div>
                )}

                {!loading && loadError && (
                    <div className="outreach-banner outreach-banner-error">
                        <span className="outreach-banner-text">{loadError}</span>
                        <button className="outreach-banner-action" onClick={this.loadInitialData}>
                            Retry
                        </button>
                    </div>
                )}

                {loading && (
                    <div className="outreach-state">
                        <div className="outreach-spinner" />
                    </div>
                )}

                {!loading && !needsAuth && !loadError && (
                    <div className="lead-form template-form">
                        {saveError && <div className="category-form-error">{saveError}</div>}
                        {saveMessage && (
                            <div className="outreach-banner outreach-banner-info">
                                <span className="outreach-banner-text">{saveMessage}</span>
                            </div>
                        )}
                        {unrecognized.length > 0 && (
                            <div className="outreach-banner outreach-banner-error">
                                <span className="outreach-banner-text">
                                    Unrecognized placeholder{unrecognized.length === 1 ? '' : 's'}: {unrecognized.map(t => `{{${t}}}`).join(', ')} — these won't be replaced when sent.
                                </span>
                            </div>
                        )}

                        <div className="template-form-columns">
                            <div className="template-form-main-col">
                                <div className="lead-form-section">
                                    <h3 className="lead-form-section-title">Template Info</h3>
                                    <div className="lead-form-grid">
                                        <div className="lead-form-field lead-form-field-wide">
                                            <label className="category-form-label">Name *</label>
                                            <input
                                                className="category-form-input"
                                                value={fields.name}
                                                onChange={this.handleTextChange('name')}
                                                placeholder="e.g. AI Automation Pitch"
                                                disabled={saving}
                                            />
                                        </div>
                                        <div className="lead-form-field">
                                            <label className="category-form-label">Category</label>
                                            <select
                                                className="category-form-input"
                                                value={fields.category_id}
                                                onChange={this.handleTextChange('category_id')}
                                                disabled={saving}
                                            >
                                                <option value="">No category</option>
                                                {categories.map(c => (
                                                    <option key={c.id} value={c.id}>{c.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="lead-form-field lead-form-checkbox-field">
                                            <label className="category-form-checkbox-row">
                                                <input
                                                    type="checkbox"
                                                    checked={fields.is_promotional}
                                                    onChange={(e) => this.setField('is_promotional', e.target.checked)}
                                                    disabled={saving}
                                                />
                                                Promotional / marketing content
                                            </label>
                                        </div>
                                        {controllers().auth.isAdmin && (
                                            <div className="lead-form-field lead-form-checkbox-field">
                                                <label className="category-form-checkbox-row">
                                                    <input
                                                        type="checkbox"
                                                        checked={fields.is_public}
                                                        onChange={(e) => this.setField('is_public', e.target.checked)}
                                                        disabled={saving}
                                                    />
                                                    Public — usable by every Lead Manager
                                                </label>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="lead-form-section">
                                    <h3 className="lead-form-section-title">Content</h3>

                                    {this.renderPlaceholderBar()}

                                    <div className="category-form-group">
                                        <label className="category-form-label">Subject</label>
                                        <input
                                            ref={this.subjectRef}
                                            className="category-form-input"
                                            value={fields.subject}
                                            onChange={this.handleTextChange('subject')}
                                            onFocus={() => this.focusField('subject')}
                                            placeholder="Optional"
                                            disabled={saving}
                                        />
                                    </div>

                                    <div className="category-form-group">
                                        <div className="settings-signature-label-row">
                                            <label className="category-form-label">Body (HTML)</label>
                                            <button type="button" className="settings-signature-upload-btn" onClick={this.handleUploadClick} disabled={saving}>
                                                Upload HTML file...
                                            </button>
                                            <input
                                                ref={this.fileInputRef}
                                                type="file"
                                                accept=".html,.htm,text/html"
                                                className="settings-signature-file-input"
                                                onChange={this.handleFileChange}
                                            />
                                        </div>
                                        <textarea
                                            ref={this.bodyHtmlRef}
                                            className="category-form-textarea template-html-textarea"
                                            value={fields.body_html}
                                            onChange={this.handleTextChange('body_html')}
                                            onFocus={() => this.focusField('body_html')}
                                            placeholder="Optional — leave blank to send plain text only"
                                            rows={10}
                                            disabled={saving}
                                        />
                                    </div>

                                    <div className="category-form-group">
                                        <label className="category-form-label">Body (Plain Text)</label>
                                        <textarea
                                            ref={this.bodyTextRef}
                                            className="category-form-textarea"
                                            value={fields.body_text}
                                            onChange={this.handleTextChange('body_text')}
                                            onFocus={() => this.focusField('body_text')}
                                            placeholder="Optional"
                                            rows={5}
                                            disabled={saving}
                                        />
                                    </div>
                                </div>

                                <div className="lead-form-section">
                                    <h3 className="lead-form-section-title">Signature (optional)</h3>
                                    <p className="outreach-muted">
                                        Leave blank to use the shared company signature. {fields.is_promotional ? (
                                            <>If you set a custom signature for this promotional template, it must include <code>{'{{unsubscribe_url}}'}</code> so recipients have a way to opt out.</>
                                        ) : (
                                            <>This template isn't marked promotional, so no unsubscribe link is required or generated — mark it Promotional above if that's not right.</>
                                        )}
                                    </p>

                                    {this.renderSignaturePlaceholderBar()}

                                    {signatureMissingUnsubscribe && (
                                        <div className="outreach-banner outreach-banner-error">
                                            <span className="outreach-banner-text">
                                                This is marked promotional, so the custom signature must include <code>{'{{unsubscribe_url}}'}</code> — add it before saving, or clear the field to use the shared signature instead.
                                            </span>
                                        </div>
                                    )}

                                    {unrecognizedSignature.length > 0 && (
                                        <div className="outreach-banner outreach-banner-error">
                                            <span className="outreach-banner-text">
                                                Unrecognized placeholder{unrecognizedSignature.length === 1 ? '' : 's'}: {unrecognizedSignature.map(t => `{{${t}}}`).join(', ')} — these won't be replaced when sent.
                                            </span>
                                        </div>
                                    )}

                                    <div className="category-form-group">
                                        <div className="settings-signature-label-row">
                                            <label className="category-form-label">Custom Signature (HTML)</label>
                                            <button type="button" className="settings-signature-upload-btn" onClick={this.handleSignatureUploadClick} disabled={saving}>
                                                Upload HTML file...
                                            </button>
                                            <input
                                                ref={this.signatureFileInputRef}
                                                type="file"
                                                accept=".html,.htm,text/html"
                                                className="settings-signature-file-input"
                                                onChange={this.handleSignatureFileChange}
                                            />
                                        </div>
                                        <textarea
                                            ref={this.signatureRef}
                                            className="category-form-textarea template-html-textarea"
                                            value={fields.custom_signature_html}
                                            onChange={this.handleTextChange('custom_signature_html')}
                                            placeholder="Optional — leave blank to use the shared signature"
                                            rows={6}
                                            disabled={saving}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="template-form-preview-col">
                                {this.renderPreview()}
                            </div>
                        </div>

                        <div className="lead-form-actions">
                            <button className="outreach-modal-cancel" onClick={this.handleCancel} disabled={saving}>
                                Cancel
                            </button>
                            <button
                                className="outreach-add-btn lead-form-compose-btn"
                                onClick={this.handlePreview}
                                disabled={saving || signatureMissingUnsubscribe}
                            >
                                {saving ? 'Saving...' : 'Save & Preview'}
                            </button>
                            <button
                                className="outreach-add-btn"
                                onClick={this.handleSave}
                                disabled={saving || signatureMissingUnsubscribe}
                            >
                                {saving ? 'Saving...' : (this.isEditMode ? 'Save Changes' : 'Create Template')}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        )
    }
}

export { TemplateFormPage }